/**
 * Simplified API Client with Unified Feedback
 * 
 * Function-based API client that uses the unified feedback system
 * and provides essential retry capabilities for improved resilience.
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/shared/utils/config';
import { ErrorType, useFeedback } from '@/shared/contexts/FeedbackContext';

// Default configuration
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Extend the InternalAxiosRequestConfig to include retryCount
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  retryCount?: number;
}

// Define API error interface
export interface ApiErrorDetails {
  code?: string;
  message?: string;
  data?: unknown;
  [key: string]: unknown;
}

/**
 * Create API client with error handling and retry capabilities
 * 
 * @param {ReturnType<typeof useFeedback>['addError']} addError - Function to add error to context
 * @returns {AxiosInstance} Configured API client
 */
export const createApiClient = (addError: ReturnType<typeof useFeedback>['addError']): AxiosInstance => {
  // Create axios instance with default config
  const client = axios.create({
    baseURL: config.urls.api || '',
    timeout: DEFAULT_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Request interceptor
  client.interceptors.request.use(
    (config: ExtendedAxiosRequestConfig) => {
      // Add authorization header if user is authenticated
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add retry count to config
      config.retryCount = config.retryCount || 0;
      
      return config;
    },
    (error: Error) => {
      return Promise.reject(error);
    }
  );
  
  // Response interceptor
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;
      
      // Check if we should retry the request
      if (
        originalRequest && 
        originalRequest.retryCount !== undefined && 
        originalRequest.retryCount < MAX_RETRIES && 
        shouldRetry(error)
      ) {
        originalRequest.retryCount += 1;
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (originalRequest.retryCount || 1)));
        
        // Retry the request
        return client(originalRequest);
      }
      
      // Handle the error if we're not retrying
      handleApiError(error, addError);
      
      return Promise.reject(error);
    }
  );
  
  return client;
};

/**
 * Determine if a request should be retried
 * 
 * @param {AxiosError} error - Error from failed request
 * @returns {boolean} Whether the request should be retried
 */
const shouldRetry = (error: AxiosError): boolean => {
  // Retry network errors
  if (!error.response) {
    return true;
  }
  
  // Retry server errors (5xx)
  if (error.response.status >= 500) {
    return true;
  }
  
  // Retry rate limiting (429)
  if (error.response.status === 429) {
    return true;
  }
  
  // Don't retry other client errors
  return false;
};

/**
 * Check if the device is online
 * 
 * @returns {boolean} Whether the device has internet connection
 */
export const isOnline = (): boolean => {
  return navigator.onLine;
};

/**
 * Handle offline state
 * 
 * @param {ReturnType<typeof useFeedback>['addError']} addError - Function to add error to context
 * @returns {void}
 */
export const handleOffline = (addError: ReturnType<typeof useFeedback>['addError']): void => {
  addError({
    type: ErrorType.API,
    message: 'You are currently offline. Some features may be unavailable.',
    details: { code: 'NET_002' }
  });
};

/**
 * API request with offline handling
 * 
 * @param {() => Promise<T>} apiCall - API call function to execute
 * @param {ReturnType<typeof useFeedback>['addError']} addError - Function to add error to context
 * @returns {Promise<T>} API response or error
 */
export const safeApiCall = async <T>(
  apiCall: () => Promise<T>,
  addError: ReturnType<typeof useFeedback>['addError']
): Promise<T> => {
  if (!isOnline()) {
    handleOffline(addError);
    return Promise.reject(new Error('Offline'));
  }
  
  try {
    return await apiCall();
  } catch (error: unknown) {
    handleApiError(error, addError);
    throw error;
  }
};

/**
 * Handle API errors with the unified feedback context
 * 
 * @param {unknown} error - Error from failed request
 * @param {ReturnType<typeof useFeedback>['addError']} addError - Function to add error to context
 * @returns {void}
 */
export const handleApiError = (error: unknown, addError: ReturnType<typeof useFeedback>['addError']): void => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    
    if (status === 401 || status === 403) {
      addError({
        type: ErrorType.AUTH,
        message: status === 401 
          ? 'Your session has expired. Please sign in again.' 
          : 'You do not have permission to perform this action.',
        details: error.response?.data
      });
    } else if (status === 400 || status === 422) {
      addError({
        type: ErrorType.VALIDATION,
        message: 'Please check your input and try again.',
        details: error.response?.data
      });
    } else {
      addError({
        type: ErrorType.API,
        message: error.response?.data?.message || 'An error occurred while communicating with the server.',
        details: error.response?.data
      });
    }
  } else if (error instanceof Error) {
    addError({
      type: ErrorType.SYSTEM,
      message: error.message || 'An unexpected error occurred.',
      details: { error }
    });
  } else {
    addError({
      type: ErrorType.SYSTEM,
      message: 'An unexpected error occurred.',
      details: { error }
    });
  }
};

// Export handleServiceError for backward compatibility
export const handleServiceError = handleApiError;

export default {
  createApiClient,
  isOnline,
  handleOffline,
  safeApiCall,
  handleApiError,
  handleServiceError
};
